import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Bot, Send, Loader2, Sparkles, CheckCircle, AlertCircle,
  FolderPlus, ListPlus, Settings2, Zap, BarChart3, Users, 
  TrendingUp, Clock, FileText, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const AIAdmin = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);
  const [activeTab, setActiveTab] = useState('reports');
  
  // Reports state
  const [teamReport, setTeamReport] = useState(null);
  const [workloadReport, setWorkloadReport] = useState(null);
  const [projectHealth, setProjectHealth] = useState(null);
  const [timeAnalysis, setTimeAnalysis] = useState(null);
  const [loadingReport, setLoadingReport] = useState(null);

  useEffect(() => {
    checkAIConfig();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkAIConfig = async () => {
    try {
      const res = await api.get('/settings/ai');
      setAiConfigured(res.data.is_configured || false);
    } catch (err) {
      setAiConfigured(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setPendingAction(null);

    try {
      const res = await api.post('/ai/chat', { message: userMessage });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        provider: res.data.provider,
        action: res.data.action
      }]);

      if (res.data.action) {
        setPendingAction(res.data.action);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la comunicarea cu AI');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Eroare: ' + (err.response?.data?.detail || 'Nu am putut procesa cererea'),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteAction = async () => {
    if (!pendingAction) return;

    setLoading(true);
    try {
      const res = await api.post('/ai/execute-action', pendingAction);
      toast.success(res.data.message);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `✅ ${res.data.message}`,
        isSuccess: true
      }]);
      setPendingAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la executarea acțiunii');
    } finally {
      setLoading(false);
    }
  };
  
  // Report loading functions
  const loadTeamPerformance = async () => {
    setLoadingReport('team');
    try {
      const res = await api.get('/ai/reports/team-performance');
      setTeamReport(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoadingReport(null);
    }
  };
  
  const loadWorkloadOptimization = async () => {
    setLoadingReport('workload');
    try {
      const res = await api.get('/ai/reports/workload-optimization');
      setWorkloadReport(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoadingReport(null);
    }
  };
  
  const loadProjectHealth = async () => {
    setLoadingReport('health');
    try {
      const res = await api.get('/ai/reports/project-health');
      setProjectHealth(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoadingReport(null);
    }
  };
  
  const loadTimeAnalysis = async () => {
    setLoadingReport('time');
    try {
      const res = await api.get('/ai/reports/time-analysis?days=30');
      setTimeAnalysis(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoadingReport(null);
    }
  };
  
  const generateCustomReport = async (query) => {
    setLoading(true);
    try {
      const res = await api.post('/ai/generate-report', { message: query });
      setMessages(prev => [...prev, 
        { role: 'user', content: `Raport: ${query}` },
        { role: 'assistant', content: res.data.report, provider: res.data.provider }
      ]);
      setActiveTab('chat');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la generarea raportului');
    } finally {
      setLoading(false);
    }
  };

  const quickPrompts = [
    { icon: ListPlus, text: "Creează 5 task-uri pentru echipă", prompt: "Creează 5 task-uri diverse pentru echipa mea pentru săptămâna aceasta" },
    { icon: FolderPlus, text: "Proiect nou", prompt: "Creează un proiect nou pentru client" },
    { icon: Zap, text: "Task-uri urgente", prompt: "Generează 3 task-uri urgente pentru proiectele active" },
    { icon: BarChart3, text: "Raport lunar", prompt: "Generează un raport de performanță pentru luna curentă" },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bot className="w-7 h-7 text-purple-500" />
              Semmy
            </h1>
            <p className="text-muted-foreground">
              Rapoarte echipă și analiză performanță
            </p>
          </div>
          <Badge variant="outline" className="bg-purple-50 text-purple-700">
            <Sparkles className="w-3 h-3 mr-1" />
            Rapoarte & Analiză
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-[400px]">
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Rapoarte & Analiză
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Chat AI
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab - Always available */}
          <TabsContent value="reports" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Team Performance Report */}
              <Card className="border-blue-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Performanță Echipă
                  </CardTitle>
                  <CardDescription>Analiza lunară a performanței echipei</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingReport === 'team' ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : teamReport ? (
                    <div className="space-y-4">
                      <Badge variant="outline" className="mb-2">{teamReport.period}</Badge>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-muted-foreground">Ore lucrate</p>
                          <p className="text-xl font-bold text-blue-600">{teamReport.summary?.total_hours_worked}h</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-muted-foreground">Task-uri finalizate</p>
                          <p className="text-xl font-bold text-green-600">{teamReport.summary?.total_tasks_completed}</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg col-span-2">
                          <p className="text-muted-foreground">Rată completare</p>
                          <p className="text-xl font-bold text-purple-600">{teamReport.summary?.team_completion_rate}%</p>
                        </div>
                      </div>
                      {teamReport.team_metrics && (
                        <div className="space-y-2 mt-4">
                          <p className="text-sm font-medium">Per membru:</p>
                          <div className="max-h-[200px] overflow-auto space-y-2">
                            {Object.entries(teamReport.team_metrics).map(([name, data]) => (
                              <div key={name} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                                <span className="font-medium">{name}</span>
                                <div className="flex gap-3 text-muted-foreground">
                                  <span>{data.hours_worked}h</span>
                                  <span>{data.tasks_completed}/{data.tasks_total} task-uri</span>
                                  <Badge variant={data.completion_rate >= 80 ? "default" : data.completion_rate >= 50 ? "secondary" : "destructive"} className="text-xs">
                                    {data.completion_rate}%
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button size="sm" variant="outline" onClick={loadTeamPerformance} className="w-full mt-2">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reîncarcă
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={loadTeamPerformance} className="w-full" data-testid="load-team-report-btn">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Generează Raport
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Workload Optimization */}
              <Card className="border-orange-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    Optimizare Workload
                  </CardTitle>
                  <CardDescription>Distribuția sarcinilor și sugestii</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingReport === 'workload' ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                    </div>
                  ) : workloadReport ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Badge variant={workloadReport.team_balance === 'balanced' ? 'default' : 'destructive'}>
                          {workloadReport.team_balance === 'balanced' ? 'Echilibrat' : 'Necesită atenție'}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {workloadReport.unassigned_tasks} neasignate
                        </span>
                      </div>
                      {workloadReport.workload_distribution && (
                        <div className="space-y-2">
                          {Object.entries(workloadReport.workload_distribution).map(([name, data]) => (
                            <div key={name} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                              <span className="font-medium">{name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{data.pending_tasks} task-uri</span>
                                <Badge 
                                  variant={data.status === 'available' ? 'secondary' : data.status === 'balanced' ? 'default' : 'destructive'}
                                  className="text-xs"
                                >
                                  {data.status === 'available' ? 'Disponibil' : data.status === 'balanced' ? 'Echilibrat' : 'Supraîncărcat'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {workloadReport.suggestions?.length > 0 && (
                        <div className="bg-orange-50 p-3 rounded-lg">
                          <p className="text-sm font-medium mb-2">Sugestii:</p>
                          <ul className="text-sm text-muted-foreground list-disc list-inside">
                            {workloadReport.suggestions.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <Button size="sm" variant="outline" onClick={loadWorkloadOptimization} className="w-full mt-2">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reîncarcă
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={loadWorkloadOptimization} className="w-full" data-testid="load-workload-report-btn">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Analizează Workload
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Project Health */}
              <Card className="border-green-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    Sănătate Proiecte
                  </CardTitle>
                  <CardDescription>Status și progres proiecte</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingReport === 'health' ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-green-500" />
                    </div>
                  ) : projectHealth ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{projectHealth.total_projects} proiecte</span>
                        {projectHealth.critical_projects?.length > 0 && (
                          <Badge variant="destructive">
                            {projectHealth.critical_projects.length} necesită atenție
                          </Badge>
                        )}
                      </div>
                      <div className="max-h-[220px] overflow-auto space-y-2">
                        {projectHealth.projects?.map((p, i) => (
                          <div key={i} className={`text-sm p-2 rounded ${p.needs_attention ? 'bg-red-50' : 'bg-slate-50'}`}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{p.project_name}</span>
                              {p.health_score && (
                                <Badge variant={p.health_score >= 80 ? 'default' : p.health_score >= 60 ? 'secondary' : 'destructive'}>
                                  {p.health_score}%
                                </Badge>
                              )}
                            </div>
                            <div className="flex justify-between text-muted-foreground text-xs mt-1">
                              <span>{p.client}</span>
                              <span>Task-uri: {p.tasks_progress} ({p.completion_rate}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button size="sm" variant="outline" onClick={loadProjectHealth} className="w-full mt-2">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reîncarcă
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={loadProjectHealth} className="w-full" data-testid="load-project-health-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      Vezi Sănătate Proiecte
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Time Analysis */}
              <Card className="border-purple-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-500" />
                    Analiză Timp
                  </CardTitle>
                  <CardDescription>Ore lucrate și eficiență (30 zile)</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingReport === 'time' ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                    </div>
                  ) : timeAnalysis ? (
                    <div className="space-y-4">
                      <div className="bg-purple-50 p-3 rounded-lg text-center">
                        <p className="text-muted-foreground text-sm">Total ore (ultimele {timeAnalysis.period_days} zile)</p>
                        <p className="text-2xl font-bold text-purple-600">{timeAnalysis.total_hours_tracked}h</p>
                      </div>
                      {timeAnalysis.hours_by_project && Object.keys(timeAnalysis.hours_by_project).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Ore per proiect:</p>
                          <div className="max-h-[150px] overflow-auto space-y-1">
                            {Object.entries(timeAnalysis.hours_by_project).map(([project, hours]) => (
                              <div key={project} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                                <span className="truncate max-w-[60%]">{project}</span>
                                <span className="font-medium text-purple-600">{hours}h</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button size="sm" variant="outline" onClick={loadTimeAnalysis} className="w-full mt-2">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reîncarcă
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={loadTimeAnalysis} className="w-full" data-testid="load-time-analysis-btn">
                      <Clock className="w-4 h-4 mr-2" />
                      Analizează Timpul
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="space-y-4">
            {!aiConfigured ? (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-orange-500" />
                    Semmy Neconfigurat
                  </CardTitle>
                  <CardDescription>
                    Pentru a utiliza Semmy, configurează API keys pentru Gemini sau Claude în Setări.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => window.location.href = '/settings'}>
                    <Settings2 className="w-4 h-4 mr-2" />
                    Mergi la Setări
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
            {/* Quick Prompts */}
            <div className="flex flex-wrap gap-2">
              {quickPrompts.map((qp, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => setInput(qp.prompt)}
                  className="text-sm"
                >
                  <qp.icon className="w-4 h-4 mr-1" />
                  {qp.text}
                </Button>
              ))}
            </div>

            {/* Chat Container */}
            <Card className="h-[500px] flex flex-col">
              <CardContent className="flex-1 overflow-auto p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="h-full flex items-center justify-center text-center text-muted-foreground">
                    <div>
                      <Bot className="w-12 h-12 mx-auto mb-4 text-purple-300" />
                      <p className="text-lg font-medium">Bună! Sunt Semmy.</p>
                      <p className="text-sm mt-2">
                        Cu ce te pot ajuta azi?
                      </p>
                    </div>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.role === 'user'
                          ? 'bg-purple-500 text-white'
                          : msg.isError
                          ? 'bg-red-100 text-red-800'
                          : msg.isSuccess
                          ? 'bg-green-100 text-green-800'
                          : 'bg-slate-100 text-slate-800'
                      }`}
                    >
                      {msg.role === 'assistant' && msg.provider && (
                        <Badge variant="outline" className="mb-2 text-xs">
                          {msg.provider === 'gemini' ? 'Gemini' : 'Claude'}
                        </Badge>
                      )}
                      <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                      
                      {msg.action && (
                        <div className="mt-3 p-2 bg-white/50 rounded border">
                          <p className="text-xs font-medium mb-2">Acțiune detectată:</p>
                          {msg.action.action === 'create_tasks' && (
                            <div>
                              <Badge className="bg-blue-100 text-blue-700">
                                <ListPlus className="w-3 h-3 mr-1" />
                                {msg.action.tasks?.length || 0} task-uri de creat
                              </Badge>
                            </div>
                          )}
                          {msg.action.action === 'create_project' && (
                            <div>
                              <Badge className="bg-green-100 text-green-700">
                                <FolderPlus className="w-3 h-3 mr-1" />
                                Proiect: {msg.action.project?.name}
                              </Badge>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-lg px-4 py-3">
                      <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </CardContent>

              {/* Pending Action */}
              {pendingAction && (
                <div className="px-4 py-3 bg-purple-50 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">
                      {pendingAction.action === 'create_tasks' 
                        ? `Crează ${pendingAction.tasks?.length} task-uri?`
                        : `Crează proiectul "${pendingAction.project?.name}"?`
                      }
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPendingAction(null)}>
                      Anulează
                    </Button>
                    <Button size="sm" onClick={handleExecuteAction} disabled={loading}>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Execută
                    </Button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Scrie un mesaj... (ex: 'Creează 3 task-uri pentru proiectul ADSEM')"
                    className="min-h-[60px] resize-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    data-testid="ai-chat-input"
                  />
                  <Button 
                    onClick={handleSend} 
                    disabled={loading || !input.trim()}
                    className="px-4"
                    data-testid="ai-send-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Tips */}
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Sfaturi:</strong> Poți cere AI-ului să creeze task-uri specifice pentru membri ai echipei, 
                  să deschidă proiecte noi cu bugete și deadline-uri, sau să genereze planuri de lucru.
                </p>
              </CardContent>
            </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AIAdmin;
